import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Stack,
  TextField,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
  Tooltip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Star as StarIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ListAlt as ListAltIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import PageLayout from '../components/PageLayout';
import SectionBlock from '../components/SectionBlock';
import { useBooking } from '../contexts/BookingContext';
import PhoneContactDialog from '../components/PhoneContactDialog';
import JobChat from '../components/JobChat';
import ClientJobTaskProgress from '../components/ClientJobTaskProgress';
import { fetchMyJobs, getJobStatusColor } from '../services/jobService';
import PeriodFilter from '../components/PeriodFilter';
import { getPeriodRange, isWithinRange } from '../utils/periodRange';

// Booking status metadata (used for the collapsible bookings list)
const BOOKING_STATUSES = {
  PENDING: { label: 'Pending', color: 'warning' },
  CONFIRMED: { label: 'Scheduled', color: 'info' },
  IN_PROGRESS: { label: 'In Progress', color: 'primary' },
  COMPLETED: { label: 'Done', color: 'success' },
  CANCELLED: { label: 'Cancelled', color: 'error' }
};

// Kanban columns are driven by JOB status
const JOB_COLUMNS = [
  { id: 'NOT_STARTED', title: 'Upcoming' },
  { id: 'IN_PROGRESS', title: 'In Progress' },
  { id: 'COMPLETED', title: 'Done' },
  { id: 'CANCELLED', title: 'Cancelled' }
];

// Map any job status onto one of the four kanban columns
const columnForJobStatus = (status) => {
  switch (status) {
    case 'IN_PROGRESS':
      return 'IN_PROGRESS';
    case 'COMPLETED':
      return 'COMPLETED';
    case 'CANCELLED':
      return 'CANCELLED';
    // NOT_STARTED, PENDING, CONFIRMED, null/undefined -> Upcoming
    default:
      return 'NOT_STARTED';
  }
};

const JOBS_POLL_INTERVAL = 20000; // 20 seconds

// Customer-facing status: the job's DB status stays NOT_STARTED until the
// technician starts work, so refine it with the pre-start timestamps.
const PRE_START_STATUSES = ['NOT_STARTED', 'PENDING', 'CONFIRMED'];
const customerJobStatus = (job) => {
  const status = job.status || 'NOT_STARTED';
  if (!PRE_START_STATUSES.includes(status)) return status;
  // Arrived is not surfaced separately; "Start job" follows right after.
  if (job.utcEnRouteTime || job.utcArrivedTime) return 'TECH_EN_ROUTE';
  return status;
};

const UserBookings = () => {
  const navigate = useNavigate();
  const { resetBooking } = useBooking();

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobsError, setJobsError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [bookingsExpanded, setBookingsExpanded] = useState(false);

  const [periodType, setPeriodType] = useState('week'); // 'list' | 'week' | 'month'
  const isList = periodType === 'list';
  const [anchor, setAnchor] = useState(new Date());

  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const [phoneDialogAction, setPhoneDialogAction] = useState('reschedule');

  useEffect(() => {
    fetchBookings();
    loadJobs();
  }, []);

  // Keep job statuses live (en route → in progress → done): refresh quietly
  // while the tab is visible, and right away when the customer returns to it.
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible') loadJobs({ silent: true });
    };
    const intervalId = setInterval(refresh, JOBS_POLL_INTERVAL);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, []);

  const loadJobs = async ({ silent = false } = {}) => {
    if (!silent) setJobsLoading(true);
    try {
      const data = await fetchMyJobs();
      setJobs(data);
      setJobsError(null);
      // Keep an open job dialog in sync with the refreshed data
      setSelectedJob((prev) => (prev ? data.find((j) => j.id === prev.id) || prev : prev));
    } catch (err) {
      // A failed background refresh keeps the last good data on screen
      if (!silent) setJobsError('Failed to load jobs. Please try again.');
    } finally {
      if (!silent) setJobsLoading(false);
    }
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/bookings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch bookings');
      }

      const data = await response.json();
      setBookings(data);
    } catch (err) {
      setError('Failed to load bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const refreshAll = () => {
    fetchBookings();
    loadJobs();
  };

  const handleRescheduleClick = () => {
    setPhoneDialogAction('reschedule');
    setPhoneDialogOpen(true);
  };

  const handleCancelClick = () => {
    setPhoneDialogAction('cancel');
    setPhoneDialogOpen(true);
  };

  const handlePhoneDialogClose = () => {
    setPhoneDialogOpen(false);
  };

  const handleNewBooking = () => {
    // Clear any previous booking data before starting fresh
    resetBooking();
    navigate('/booking', {
      state: {
        from: '/bookings',
        flowStartedAsGuest: false
      }
    });
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'PPP');
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const formatTime = (dateString) => {
    try {
      return format(new Date(dateString), 'p');
    } catch (e) {
      return 'Invalid Time';
    }
  };

  const formatJobAddress = (address) => {
    if (!address) return '';
    if (typeof address === 'string') return address;
    return [address.street, address.city, address.state].filter(Boolean).join(', ');
  };

  // --- Jobs (kanban) ---------------------------------------------------------

  // List mode shows every job (no date range)
  const { start: rangeStart, end: rangeEnd } = useMemo(
    () => (periodType === 'list' ? { start: null, end: null } : getPeriodRange(periodType, anchor)),
    [periodType, anchor]
  );

  const filteredJobs = jobs.filter((job) => {
    if (!isList && !isWithinRange(job.scheduledDate, rangeStart, rangeEnd)) return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (job.jobType || '').toLowerCase().includes(term) ||
      customerJobStatus(job).replace(/_/g, ' ').toLowerCase().includes(term) ||
      formatJobAddress(job.address).toLowerCase().includes(term)
    );
  });

  const renderJobCard = (job) => {
    const displayStatus = customerJobStatus(job);
    const statusColor = getJobStatusColor(displayStatus);
    const address = formatJobAddress(job.address);
    return (
      <motion.div
        key={job.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
      >
        <Card
          elevation={2}
          onClick={() => setSelectedJob(job)}
          sx={{
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-2px)',
              transition: 'all 0.3s ease'
            }
          }}
        >
          <CardContent>
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Chip
                  label={displayStatus.replace(/_/g, ' ')}
                  size="small"
                  sx={{ bgcolor: statusColor, color: '#fff', fontWeight: 600 }}
                />
                {job.jobType && (
                  <Typography variant="caption" color="text.secondary">
                    {job.jobType.replace(/_/g, ' ')}
                  </Typography>
                )}
              </Stack>

              <Stack direction="row" alignItems="center" spacing={1}>
                <CalendarIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  {job.scheduledDate ? formatDate(job.scheduledDate) : 'TBD'}
                </Typography>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={1}>
                <TimeIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  {job.scheduledDate ? formatTime(job.scheduledDate) : 'TBD'}
                </Typography>
              </Stack>

              {address && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LocationIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {address}
                  </Typography>
                </Stack>
              )}

              <Typography variant="caption" color="primary">
                <ListAltIcon sx={{ fontSize: 14, verticalAlign: 'text-bottom', mr: 0.5 }} />
                Tap for tasks &amp; messages
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  // --- Bookings (collapsible list) ------------------------------------------

  const renderBookingActions = (booking) => {
    const buttons = [
      <Tooltip title="View Details" key="view">
        <IconButton
          size="small"
          color="primary"
          onClick={() => navigate(`/bookings/${booking.id}`)}
        >
          <ViewIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ];

    if (booking.status === 'PENDING') {
      buttons.push(
        <Tooltip title="Reschedule" key="reschedule">
          <IconButton size="small" color="primary" onClick={handleRescheduleClick}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    }

    if (booking.status === 'PENDING' || booking.status === 'CONFIRMED') {
      buttons.push(
        <Tooltip title="Cancel" key="cancel">
          <IconButton size="small" color="error" onClick={handleCancelClick}>
            <CancelIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    }

    if (booking.status === 'COMPLETED' && !booking.reviewed) {
      buttons.push(
        <Tooltip title="Leave Review" key="review">
          <IconButton size="small" color="secondary">
            <StarIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    }

    return <Stack direction="row" spacing={0.5}>{buttons}</Stack>;
  };

  const renderBookingRow = (booking) => {
    const statusMeta = BOOKING_STATUSES[booking.status] || { label: booking.status, color: 'default' };
    return (
      <Stack
        key={booking.id}
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body1" fontWeight={600} noWrap>
            {booking.mainService || booking.service || 'Cleaning Service'}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {formatDate(booking.serviceDate)} · {formatTime(booking.serviceTime)}
            {booking.address ? ` · ${booking.address}` : ''}
          </Typography>
        </Box>

        <Stack direction="row" spacing={2} alignItems="center">
          <Chip label={statusMeta.label} size="small" color={statusMeta.color} />
          {booking.paymentStatus === 'REFUNDED' ? (
            <Chip label="Refunded" size="small" color="warning" variant="outlined" />
          ) : (booking.paymentStatus === 'RELEASED' || booking.paymentStatus === 'HELD') ? (
            <Chip label="Paid" size="small" color="success" variant="outlined" />
          ) : (
            <Chip label="Unpaid" size="small" variant="outlined" />
          )}
          <Typography variant="body2" fontWeight={600} sx={{ minWidth: 56, textAlign: 'right' }}>
            ${booking.totalAmount}
          </Typography>
          {renderBookingActions(booking)}
        </Stack>
      </Stack>
    );
  };

  if (loading && jobsLoading) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Box sx={{ maxWidth: '100vw', overflowX: 'hidden' }}>
        {/* Header */}
        <SectionBlock bgcolor="primary.light" py={{ xs: 4, md: 6 }} maxWidth="lg">
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            spacing={2}
          >
            <Typography variant="h4" fontWeight={700}>
              My Services
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              width={{ xs: '100%', md: 'auto' }}
            >
              <TextField
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                sx={{ minWidth: 200 }}
                InputProps={{
                  startAdornment: <FilterIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />

              <Button
                variant="contained"
                onClick={handleNewBooking}
                startIcon={<AddIcon />}
                sx={{ whiteSpace: 'nowrap' }}
              >
                New Booking
              </Button>

              <Tooltip title="Refresh">
                <IconButton
                  onClick={refreshAll}
                  color="primary"
                  sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </SectionBlock>

        {/* Jobs Kanban */}
        <SectionBlock maxWidth="lg" py={4}>
          <PeriodFilter
            periodType={periodType}
            anchor={anchor}
            start={rangeStart}
            end={rangeEnd}
            onPeriodTypeChange={setPeriodType}
            onAnchorChange={setAnchor}
            showList
          />

          {jobsError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {jobsError}
            </Alert>
          )}

          {jobsLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress size={40} />
            </Box>
          ) : isList ? (
            // List: today and later in date order, then past visits most recent first
            (() => {
              const startOfToday = new Date();
              startOfToday.setHours(0, 0, 0, 0);
              const time = (job) => (job.scheduledDate ? new Date(job.scheduledDate).getTime() : 0);
              const sections = [
                {
                  id: 'upcoming',
                  title: 'Upcoming',
                  jobs: filteredJobs.filter((j) => time(j) >= startOfToday.getTime()).sort((a, b) => time(a) - time(b)),
                  empty: 'No upcoming services.',
                },
                {
                  id: 'past',
                  title: 'Past',
                  jobs: filteredJobs.filter((j) => time(j) < startOfToday.getTime()).sort((a, b) => time(b) - time(a)),
                  empty: 'No past services yet.',
                },
              ];
              return (
                <Box sx={{ maxWidth: 720, mx: 'auto' }}>
                  {sections.map((section) => (
                    <Box key={section.id} mb={4}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                        <Typography variant="h6" fontWeight={600}>{section.title}</Typography>
                        <Chip label={section.jobs.length} size="small" />
                      </Stack>
                      <Stack spacing={2}>
                        <AnimatePresence>
                          {section.jobs.map((job) => renderJobCard(job))}
                        </AnimatePresence>
                        {section.jobs.length === 0 && (
                          <Typography variant="body2" color="text.secondary">{section.empty}</Typography>
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Box>
              );
            })()
          ) : (
            <Grid container spacing={2}>
              {JOB_COLUMNS.map((column) => {
                const columnJobs = filteredJobs.filter(
                  (job) => columnForJobStatus(job.status) === column.id
                );
                return (
                  <Grid item xs={12} md={3} key={column.id}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: 'grey.100',
                        height: '100%',
                        minHeight: 400,
                        borderRadius: 2
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                        <Typography variant="h6" fontWeight={600}>
                          {column.title}
                        </Typography>
                        <Chip
                          label={columnJobs.length}
                          size="small"
                          sx={{
                            bgcolor: getJobStatusColor(column.id),
                            color: '#fff',
                            fontWeight: 600
                          }}
                        />
                      </Stack>

                      <Stack spacing={2}>
                        <AnimatePresence>
                          {columnJobs.map((job) => renderJobCard(job))}
                        </AnimatePresence>
                        {columnJobs.length === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            No jobs.
                          </Typography>
                        )}
                      </Stack>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </SectionBlock>

        {/* Bookings (collapsible) */}
        <SectionBlock maxWidth="lg" py={4}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ p: 2, cursor: 'pointer' }}
              onClick={() => setBookingsExpanded((prev) => !prev)}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h6" fontWeight={600}>
                  My Bookings
                </Typography>
                <Chip label={`${bookings.length} total`} size="small" />
              </Stack>
              <IconButton size="small">
                {bookingsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Stack>

            <Collapse in={bookingsExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ px: 2, pb: 2 }}>
                {loading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress size={32} />
                  </Box>
                ) : bookings.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 2 }}>
                    No bookings yet.
                  </Typography>
                ) : (
                  bookings.map((booking) => renderBookingRow(booking))
                )}
              </Box>
            </Collapse>
          </Paper>
        </SectionBlock>

        {/* Job detail dialog */}
        <Dialog
          open={Boolean(selectedJob)}
          onClose={() => setSelectedJob(null)}
          maxWidth="sm"
          fullWidth
        >
          {selectedJob && (
            <>
              <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={customerJobStatus(selectedJob).replace(/_/g, ' ')}
                      size="small"
                      sx={{
                        bgcolor: getJobStatusColor(customerJobStatus(selectedJob)),
                        color: '#fff',
                        fontWeight: 600
                      }}
                    />
                    {selectedJob.jobType && (
                      <Typography variant="subtitle1" fontWeight={600}>
                        {selectedJob.jobType.replace(/_/g, ' ')}
                      </Typography>
                    )}
                  </Stack>
                  <IconButton size="small" onClick={() => setSelectedJob(null)}>
                    <CloseIcon />
                  </IconButton>
                </Stack>
              </DialogTitle>

              <DialogContent dividers>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    {selectedJob.scheduledDate ? formatDate(selectedJob.scheduledDate) : 'TBD'}
                  </Typography>
                  <TimeIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    {selectedJob.scheduledDate ? formatTime(selectedJob.scheduledDate) : 'TBD'}
                  </Typography>
                </Stack>

                {formatJobAddress(selectedJob.address) && (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <LocationIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {formatJobAddress(selectedJob.address)}
                    </Typography>
                  </Stack>
                )}

                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Task Progress
                </Typography>
                <ClientJobTaskProgress jobId={selectedJob.id} />

                <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
                  Messages
                </Typography>
                <JobChat jobId={selectedJob.id} compact />
              </DialogContent>

              <DialogActions>
                <Button onClick={() => setSelectedJob(null)}>Close</Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Phone Contact Dialog for Reschedule/Cancel */}
        <PhoneContactDialog
          open={phoneDialogOpen}
          onClose={handlePhoneDialogClose}
          action={phoneDialogAction}
        />
      </Box>
    </PageLayout>
  );
};

export default UserBookings;
